import api from '../../../api/client'
import { invalidateEntity } from '../../../api/cacheInvalidation'

export async function fetchProductivityDashboardSummary(params = {}) {
  const { data } = await api.get('/productivity/dashboard', { params })
  return data || {}
}

export async function fetchProductivityDashboardDrilldown(params = {}) {
  const { data } = await api.get('/productivity/dashboard/drilldown', { params })
  return data || { columns: [], rows: [], total: 0, page: 1, pageSize: 25 }
}

export async function exportProductivityDashboardDrilldown(params = {}) {
  const response = await api.get('/productivity/dashboard/export', {
    params,
    responseType: 'blob',
  })
  const disposition = response.headers?.['content-disposition'] || ''
  const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || 'productivity-drill.csv'
  return { blob: response.data, filename }
}

export async function fetchProductivitySessionStatusLegend() {
  const { data } = await api.get('/productivity/sessions/status-legend')
  return Array.isArray(data?.items) ? data.items : []
}

export async function updateProductivitySessionStatusLegend(code, payload = {}) {
  const { data } = await api.put(`/productivity/sessions/status-legend/${encodeURIComponent(code)}`, payload)
  invalidateEntity.productivity()
  return data || {}
}

export async function fetchProductivitySessions(params = {}) {
  const { data } = await api.get('/productivity/sessions', {
    params: {
      page: 1,
      page_size: 10,
      includeTotal: true,
      ...params,
    },
  })
  return data || {
    table: { items: [], page: 1, pageSize: 10, hasMore: false, total: 0 },
    productiveOutputMatrix: { bars: [] },
  }
}

export async function fetchProductivityUserOptions(params = {}) {
  const { data } = await api.get('/options/users', {
    params: {
      page: 1,
      page_size: 200,
      ...params,
    },
  })
  return Array.isArray(data) ? data : []
}

export async function fetchProductivityDepartmentOptions(params = {}) {
  const { data } = await api.get('/options/departments', {
    params: {
      page: 1,
      page_size: 200,
      ...params,
    },
  })
  return Array.isArray(data) ? data : []
}

export async function fetchProductivityCompanyOptions(params = {}) {
  const { data } = await api.get('/options/companies', {
    params: {
      page: 1,
      page_size: 200,
      ...params,
    },
  })
  return Array.isArray(data) ? data : []
}

export async function fetchProductivityAdjustments(params = {}) {
  const { data } = await api.get('/productivity/adjustments', {
    params: {
      page: 1,
      pageSize: 10,
      includeTotal: true,
      ...params,
    },
  })
  return data || { items: [], page: 1, pageSize: 10, hasMore: false, total: 0 }
}

export async function fetchProductivityAdjustmentApprovalQueue(params = {}) {
  const { data } = await api.get('/productivity/adjustments/approval-queue', {
    params: {
      page: 1,
      pageSize: 10,
      includeTotal: true,
      ...params,
    },
  })
  return data || { items: [], page: 1, pageSize: 10, hasMore: false, total: 0 }
}

export async function fetchProductivityAdjustmentApprovalUsers(params = {}) {
  const { data } = await api.get('/productivity/adjustments/approval-options/users', { params })
  return Array.isArray(data) ? data : []
}

export async function approveProductivityAdjustment(adjustmentId, payload = {}) {
  const { data } = await api.patch(`/productivity/adjustments/${adjustmentId}/approve`, payload)
  invalidateEntity.productivity()
  return data || {}
}

export async function rejectProductivityAdjustment(adjustmentId, payload = {}) {
  const { data } = await api.patch(`/productivity/adjustments/${adjustmentId}/reject`, payload)
  invalidateEntity.productivity()
  return data || {}
}

export async function updateProductivityAdjustmentRequestedTime(adjustmentId, payload = {}) {
  const { data } = await api.patch(`/productivity/adjustments/${adjustmentId}/requested-time`, payload)
  invalidateEntity.productivity()
  return data || {}
}

export async function resubmitProductivityAdjustment(adjustmentId) {
  const { data } = await api.patch(`/productivity/adjustments/${adjustmentId}/resubmit`)
  invalidateEntity.productivity()
  return data || {}
}

export async function fetchAppActivity(filters) {
  const params =
    typeof filters === 'string'
      ? (filters ? { target_date: filters } : {})
      : (filters || {})
  const { data } = await api.get('/productivity/dashboard/activity/apps', { params })
  return {
    items: Array.isArray(data?.apps) ? data.apps : [],
    total: Number(data?.total || (Array.isArray(data?.apps) ? data.apps.length : 0)),
    page: Number(data?.page || params.page || 1),
    pageSize: Number(data?.pageSize || params.page_size || 5),
  }
}

export async function fetchDomainActivity(filters) {
  let params = {}
  if (typeof filters === 'string') {
    params = filters ? { target_date: filters } : {}
  } else if (filters && typeof filters === 'object') {
    params = filters
  }
  const { data } = await api.get('/productivity/dashboard/activity/domains', { params })
  return {
    items: Array.isArray(data?.domains) ? data.domains : [],
    total: Number(data?.total || (Array.isArray(data?.domains) ? data.domains.length : 0)),
    page: Number(data?.page || params.page || 1),
    pageSize: Number(data?.pageSize || params.page_size || 5),
  }
}

export async function fetchAmbientRedDomains(filters) {
  let params = {}
  if (typeof filters === 'string') {
    params = filters ? { target_date: filters } : {}
  } else if (filters && typeof filters === 'object') {
    params = filters
  }
  const { data } = await api.get('/productivity/dashboard/activity/ambient', { params })
  return {
    items: Array.isArray(data?.ambient) ? data.ambient : [],
    total: Number(data?.total || (Array.isArray(data?.ambient) ? data.ambient.length : 0)),
    page: Number(data?.page || params.page || 1),
    pageSize: Number(data?.pageSize || params.page_size || 5),
  }
}

export async function bulkApproveProductivityAdjustments(payload) {
  const { data } = await api.patch('/productivity/adjustments/bulk-approve', payload)
  invalidateEntity.productivity()
  return data
}

export async function bulkRejectProductivityAdjustments(payload) {
  const { data } = await api.patch('/productivity/adjustments/bulk-reject', payload)
  invalidateEntity.productivity()
  return data
}

export async function fetchProductivityAdjustmentApprovalProjects(search = '', page = 1, pageSize = 20) {
  const params = { search, page, page_size: pageSize, archive: false }
  const { data } = await api.get('/projects/', { params })
  const rawItems = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
  const items = rawItems.map((item) => ({
    value: String(item.id),
    label: item.project_code ? `${item.name} (${item.project_code})` : item.name,
  }))
  const hasMore = rawItems.length === pageSize
  return { items, hasMore }
}

export async function fetchProductivityAdjustmentApprovalTasks(search = '', page = 1, pageSize = 20, context = {}) {
  const projectId = typeof context === 'object' ? context?.project_id : context
  const params = {
    search,
    page,
    page_size: pageSize,
    includeTotal: false,
    archive: false,
    fromDate: '1900-01-01',
    toDate: '9999-12-31',
  }
  if (projectId && projectId !== 'All' && projectId !== 'all') {
    params.project_id = Number(projectId)
  }
  const { data } = await api.get('/tasks/', { params })
  const rawItems = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
  const items = rawItems.map((item) => ({
    value: String(item.id),
    label: item.task_code ? `${item.title || item.task_title || item.name} (${item.task_code})` : (item.title || item.task_title || item.name),
  }))
  const hasMore = rawItems.length === pageSize
  return { items, hasMore }
}

export async function fetchProductivityAdjustmentApprovalDetail(adjustmentId) {
  const { data } = await api.get(`/productivity/adjustments/${adjustmentId}`)
  return data
}


